import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import { api } from './auth';
import type {
  Area,
  Availability,
  Me,
  MyBooking,
  RangeAvailability,
  RangeBookingResult,
  Site,
  Slot,
} from './types';

export function useMe() {
  return useQuery({
    queryKey: ['me'],
    queryFn: () => api<{ user: Me }>('/me').then((r) => r.user),
    staleTime: 10 * 60 * 1000,
  });
}

export function useSites() {
  return useQuery({
    queryKey: ['sites'],
    queryFn: () => api<{ sites: Site[] }>('/sites').then((r) => r.sites),
    staleTime: 60 * 60 * 1000,
  });
}

export function useAreas(siteId: string | undefined) {
  return useQuery({
    queryKey: ['areas', siteId],
    queryFn: () => api<{ areas: Area[] }>(`/sites/${siteId}/areas`).then((r) => r.areas),
    enabled: Boolean(siteId),
    staleTime: 60 * 60 * 1000,
  });
}

export function useAvailability(areaId: string | undefined, date: string) {
  return useQuery({
    queryKey: ['availability', areaId, date],
    queryFn: () => api<Availability>(`/areas/${areaId}/availability?date=${date}`),
    enabled: Boolean(areaId),
    // Short: at 07:00 people are watching for a released bay, and stale
    // availability is the one thing that makes the app feel broken.
    staleTime: 15 * 1000,
    refetchOnWindowFocus: true,
  });
}

/**
 * Every desk across a range of days, for booking one desk for a week.
 *
 * Keyed under ['availability', ...] on purpose: booking or cancelling
 * invalidates that whole key, so a range view refreshes for the same
 * reasons a single-day one does.
 */
export function useRangeAvailability(
  areaId: string | undefined,
  from: string,
  to: string,
  slot: Slot,
) {
  const usable = Boolean(areaId) && Boolean(from) && Boolean(to) && to >= from;
  return useQuery({
    queryKey: ['availability', 'range', areaId, from, to, slot],
    queryFn: () =>
      api<RangeAvailability>(
        `/areas/${areaId}/range-availability?from=${from}&to=${to}&slot=${slot}`,
      ),
    enabled: usable,
    staleTime: 15 * 1000,
    refetchOnWindowFocus: true,
  });
}

/** Which floor to open on, per resource kind. */
export function usePreferredAreas() {
  return useQuery({
    queryKey: ['preferences', 'areas'],
    queryFn: () =>
      api<{ areas: Record<string, string | undefined> }>('/preferences/areas').then(
        (r) => r.areas,
      ),
    staleTime: 60 * 60 * 1000,
  });
}

export function useSetPreferredArea() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ kind, areaId }: { kind: string; areaId: string | null }) =>
      areaId === null
        ? api(`/preferences/areas/${kind}`, { method: 'DELETE' })
        : api('/preferences/areas', {
            method: 'PUT',
            body: JSON.stringify({ kind, areaId }),
          }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['preferences'] }),
  });
}

export function useMyBookings(from: string, to: string) {
  return useQuery({
    queryKey: ['bookings', from, to],
    queryFn: () =>
      api<{ bookings: MyBooking[] }>(`/bookings/mine?from=${from}&to=${to}`).then(
        (r) => r.bookings,
      ),
  });
}

function useBookingMutation<TVariables, TResult = unknown>(
  fn: (variables: TVariables) => Promise<TResult>,
): UseMutationResult<TResult, Error, TVariables> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fn,
    // Any change to a booking can affect what everyone else sees, so
    // both the map and the personal list are refetched rather than
    // patched locally.
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: ['availability'] }),
        client.invalidateQueries({ queryKey: ['bookings'] }),
      ]);
    },
  });
}

export function useCreateBooking() {
  return useBookingMutation<{ resourceId: string; date: string; slot: Slot }>((body) =>
    api('/bookings', { method: 'POST', body: JSON.stringify(body) }),
  );
}

/**
 * One desk, several days. Resolves with what was created and what was
 * skipped, so the caller can say "four days booked, Wednesday you
 * already had" rather than just succeeding quietly.
 */
export function useCreateRangeBooking() {
  return useBookingMutation<
    { resourceId: string; from: string; to: string; slot: Slot },
    RangeBookingResult
  >((body) =>
    api<RangeBookingResult>('/bookings/range', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  );
}

export function useCancelBooking() {
  return useBookingMutation<string>((id) => api(`/bookings/${id}`, { method: 'DELETE' }));
}

export function useUpdateBooking() {
  return useBookingMutation<{ id: string; slot?: Slot; resourceId?: string; date?: string }>(
    ({ id, ...body }) => api(`/bookings/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  );
}
